import { useTranslation } from 'react-i18next';
import { useShowSitePartitioningItems } from 'utils/customHooks/useShowSitePartitioningItems';
import intersection from 'lodash/intersection';
import { useOpQuery } from 'utils/customHooks/useOpQuery';
import { ComponentProps } from 'react';
import { OpSiteSpecificUserDataFetchTransferProps } from './types';
import { OpDataFetchTransfer } from '../OpDataFetchTransfer/OpDataFetchTransfer';
import { OpText } from '../OpText/OpText';

/**
 * Component that wraps `OpDataFetchTransfer` to fetch and display users in a way appropriate for
 * site specific access when enabled.
 *
 * @param {OpSiteSpecificUserDataFetchTransferProps} props - The properties for the component.
 * @param {number} props.orgId - The organization ID used to fetch site data.
 * @param {object} props.existingItemsQueryOptions - Query options for fetching existing items.
 * @param {object} props.existingItemIdsQueryOptions - Query options for fetching existing item IDs.
 * @param {string} [props.existingItemsLabel] - Label for the existing items section.
 * @param {function} [props.createDisabledAvailableItem] - Function that takes an item and returns a boolean indicating if it should be disabled.
 * @param {function} [props.createAvailableItemTooltip] - Function that takes an item and returns a string for the tooltip.
 *
 * @returns {JSX.Element} The rendered component.
 */
export const OpSiteSpecificUserDataFetchTransfer = ({
  orgId,
  existingItemsQueryOptions,
  existingItemIdsQueryOptions,
  existingItemsLabel,
  createDisabledAvailableItem,
  createAvailableItemTooltip,
  ...opSiteSpecificUserDataFetchTransferProps
}: OpSiteSpecificUserDataFetchTransferProps) => {
  const { t } = useTranslation();

  const { isShowingSitePartitioningItems } =
    useShowSitePartitioningItems(orgId);

  const { data: sitesData } = useOpQuery({
    apiEndpointName: 'listSites',
    parameters: [orgId],
    select: (heliumResponse) => heliumResponse?.json?.data,
  });

  const createLabel: ComponentProps<
    typeof OpDataFetchTransfer
  >['createLabel'] = (record) => (
    <div>
      <OpText ellipsis>{record.title || '--'}</OpText>
      <OpText
        ellipsis
        size="small"
        className="op-data-fetch-transfer__sub-label"
      >
        {record.description}
      </OpText>
    </div>
  );

  return (
    <OpDataFetchTransfer
      availableItemsParams={{
        queryOptions: {
          apiEndpointName: 'listUsersPost',
          parameters: [
            orgId,
            {
              preFilter: 'status:(!=I)',
              ...(isShowingSitePartitioningItems && {
                options: 'withUserSites',
              }),
            },
          ],
        },
        createTitle: (item) => item.identity.fullName || '--',
        createDescription: (item) => item.identity.email,
        createDisabled: createDisabledAvailableItem,
        createTooltip: createAvailableItemTooltip,
      }}
      existingItemsParams={{
        queryOptions: existingItemsQueryOptions,
        createTitle: (item) => item.identity.fullName || '--',
        createDescription: (item) => item.identity.email,
        createDisabled: (item) => {
          if (!isShowingSitePartitioningItems) {
            return false;
          }

          const userSiteIds =
            item.sites?.map(({ id }: { id: number }) => id) || [];
          const siteIds = sitesData?.map(({ id }: { id: number }) => id) || [];

          /**
           * If the user has site(s) assigned to it and the signed in user
           * doesn't have access to any of those sites, disable the user
           */
          return (
            userSiteIds.length > 0 && !intersection(userSiteIds, siteIds).length
          );
        },
        createTooltip: (item) => {
          if (!isShowingSitePartitioningItems) {
            return null;
          }

          const userSiteIds =
            item.sites?.map(({ id }: { id: number }) => id) || [];
          const siteIds = sitesData?.map(({ id }: { id: number }) => id) || [];

          /**
           * If the user has site(s) assigned to it and the signed in user
           * doesn't have access to any of those sites, disable the user
           */
          const isRoleRestricted =
            userSiteIds.length > 0 &&
            !intersection(userSiteIds, siteIds).length;

          return isRoleRestricted
            ? t('Your site-specific role prevents management of this user')
            : null;
        },
      }}
      existingItemIdsParams={{
        queryOptions: existingItemIdsQueryOptions,
      }}
      createLabel={createLabel}
      existingItemsLabel={existingItemsLabel}
      // Custom filter option that checks both title and description
      filterOption={(inputValue, option) => {
        if (option.title && option.description) {
          return (
            option.title.toLowerCase().indexOf(inputValue.toLowerCase()) > -1 ||
            option.description.toLowerCase().indexOf(inputValue.toLowerCase()) >
              -1
          );
        }

        return false;
      }}
      {...opSiteSpecificUserDataFetchTransferProps}
    />
  );
};
